import { useRef, useState } from "react";
import { Icon } from "@iconify/react";
import type { Medication } from "@/shared/types";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";

interface MedicationsSectionProps { medications: Medication[]; }

const statusLabels: Record<string, string> = {
  active: "Active", discontinued: "Discontinued",
  on_hold: "On Hold", completed: "Completed",
};

const routeLabels: Record<string, string> = {
  oral: "By Mouth", topical: "Applied to skin",
  injection: "Injection", inhalation: "Inhaled", other: "Other",
};

function formatDate(dateString: string): string {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const date = new Date(dateString + "T00:00:00");
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

// Same RedIcon as DiagnosisSection
function RedIcon({ icon, size = "md" }: { icon: string; size?: "xs" | "sm" | "md" | "lg" }) {
  return (
    <div className={cn(
      "flex shrink-0 items-center justify-center rounded-xl bg-[#E4002B]/50",
      size === "xs" && "h-6 w-6",
      size === "sm" && "h-7 w-7",
      size === "md" && "h-9 w-9",
      size === "lg" && "h-11 w-11"
    )}>
      <Icon icon={icon} className={cn(
        "!text-white",
        size === "xs" && "h-3 w-3",
        size === "sm" && "h-3 w-3",
        size === "md" && "h-4 w-4",
        size === "lg" && "h-5 w-5"
      )} />
    </div>
  );
}

function StatCard({
  icon, label, value,
}: {
  icon: string; label: string; value: number;
}) {
  return (
    <div className="flex min-h-[110px] flex-col justify-between rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg">
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E4002B]/50">
          <Icon icon={icon} className="h-4 w-4 !text-white" />
        </div>
        <p className="text-sm font-semibold text-[#111827]">{label}</p>
      </div>
      <p className="text-right text-3xl font-bold text-[#111827]">{value}</p>
    </div>
  );
}

function MedicationRow({
  med, isSelected, onSelect,
}: {
  med: Medication; isSelected: boolean; onSelect: () => void;
}) {
  const isActive = med.status === "active";

  return (
    <Button
      variant="outline"
      onClick={onSelect}
      className={cn(
        "!flex !h-auto !w-full !items-center !justify-between !gap-4 !whitespace-normal !rounded-2xl !bg-white !p-4 !text-left !shadow-sm hover:!bg-[#FFF8F8] hover:!shadow-md",
        isSelected ? "!border-[#E5E7EB] !bg-[#FFF8F8]" : "!border-[#E5E7EB]"
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-extrabold text-[#0A2947]">{med.name}</p>
          <span className="rounded-full bg-[#FDECEF] px-2 py-0.5 text-[10px] font-bold text-[#E4002B]">
            {isActive ? med.dosage : statusLabels[med.status]}
          </span>
        </div>
        <p className="mt-0.5 break-words text-xs text-[#4B5563]">{med.frequency} - {med.purpose}</p>
        <p className="mt-1.5 text-xs text-[#9CA3AF]">
          Prescribed By <span className="font-semibold text-[#4B5563]">{med.prescribedBy}</span>
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
        {isActive ? (
          <span className="flex items-center gap-1.5 font-semibold text-[#111827]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#16A34A]" /> Active
          </span>
        ) : (
          <>
            <span className="text-[#9CA3AF]">Last Updated</span>
            <span className="font-semibold text-[#4B5563]">{formatDate(med.endDate ?? med.prescribedDate)}</span>
          </>
        )}
      </div>
    </Button>
  );
}

function MedicationGroup({
  title, meds, selectedId, onSelect, groupRef,
}: {
  title: string; meds: Medication[]; selectedId?: string; onSelect: (med: Medication) => void;
  groupRef: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <div ref={groupRef} className="grid gap-3">
      <h3 className="text-sm font-extrabold text-[#111827]">{title}</h3>
      {meds.length > 0 ? (
        <div className="grid gap-4">
          {meds.map((med) => (
            <MedicationRow key={med.id} med={med} isSelected={med.id === selectedId} onSelect={() => onSelect(med)} />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-[#E5E7EB] bg-white py-6 text-center">
          <p className="text-xs font-semibold text-[#9CA3AF]">Nothing here yet</p>
        </div>
      )}
    </div>
  );
}

function MedicationDetailPanel({
  medication, onClose,
}: {
  medication: Medication | null; onClose: () => void;
}) {
  if (!medication) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center rounded-3xl border border-[#E5E7EB] bg-white p-6 text-center shadow-sm">
        <RedIcon icon="lucide:pill" size="lg" />
        <p className="mt-4 text-sm font-extrabold text-[#0A2947]">Select a medication</p>
        <p className="mt-1 text-xs font-semibold text-[#9CA3AF]">Choose a medication from the list to view its details</p>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-4">
          <RedIcon icon="lucide:pill" size="lg" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-lg font-extrabold text-[#0A2947]">{medication.name}</h2>
              <span className="rounded-full bg-[#FDECEF] px-2.5 py-0.5 text-xs font-semibold text-[#E4002B]">
                {statusLabels[medication.status]}
              </span>
            </div>
            {medication.genericName && (
              <p className="mt-1 text-sm text-[#9CA3AF]">Generic: {medication.genericName}</p>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={onClose}
          className="!flex !h-8 !w-8 !shrink-0 !items-center !justify-center !rounded-full !p-0 !text-[#9CA3AF] hover:!bg-[#F3F4F6] hover:!text-[#111827]"
        >
          <Icon icon="lucide:x" className="h-5 w-5" />
        </Button>
      </div>

      {/* How to Take */}
      <div className="mb-6 rounded-2xl border border-[#E4002B]/15 bg-[#FFF0F3] p-5">
        <h3 className="mb-3 text-sm font-extrabold text-[#E4002B]">How to Take</h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <p className="text-xs text-[#9CA3AF]">Dosage</p>
            <p className="mt-0.5 text-sm font-semibold text-[#0A2947]">{medication.dosage}</p>
          </div>
          <div>
            <p className="text-xs text-[#9CA3AF]">Frequency</p>
            <p className="mt-0.5 text-sm font-semibold text-[#0A2947]">{medication.frequency}</p>
          </div>
          <div>
            <p className="text-xs text-[#9CA3AF]">Route</p>
            <p className="mt-0.5 text-sm font-semibold text-[#0A2947]">{routeLabels[medication.route]}</p>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-xs text-[#9CA3AF]">Instructions</p>
          <p className="mt-0.5 text-sm text-[#4B5563]">{medication.instructions}</p>
        </div>
      </div>

      {/* Purpose */}
      <div className="mb-6">
        <h3 className="mb-2 text-sm font-extrabold text-[#0A2947]">Purpose</h3>
        <p className="text-sm text-[#4B5563]">{medication.purpose}</p>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { icon: "lucide:calendar",  label: "Start Date",        value: formatDate(medication.startDate) },
          { icon: "lucide:user",      label: "Prescribed By",     value: medication.prescribedBy },
          ...(medication.pharmacy ? [{ icon: "lucide:building-2", label: "Pharmacy", value: medication.pharmacy }] : []),
          { icon: "lucide:refresh-cw", label: "Refills Remaining", value: medication.refillsRemaining !== null ? String(medication.refillsRemaining) : "Not specified" },
          { icon: "lucide:clock",     label: "Last Update",       value: formatDate(medication.endDate ?? medication.prescribedDate) },
        ].map(({ icon, label, value }) => (
          <div key={label} className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-3">
            <div className="flex items-start gap-1.5">
              <RedIcon icon={icon} size="xs" />
              <p className="text-xs leading-tight text-[#9CA3AF]">{label}</p>
            </div>
            <p className="mt-1.5 text-sm font-semibold text-[#0A2947]">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MedicationsSection({ medications }: MedicationsSectionProps) {
  const activeMeds     = medications.filter(m => m.status === "active");
  const inactiveMeds   = medications.filter(m => m.status !== "active");
  const lowRefillMeds  = activeMeds.filter(m => m.refillsRemaining !== null && m.refillsRemaining <= 1);

  const [selectedMedication, setSelectedMedication] = useState<Medication | null>(medications[0] ?? null);

  const allRef = useRef<HTMLDivElement>(null);
  const lowRefillsRef = useRef<HTMLDivElement>(null);
  const pastRef = useRef<HTMLDivElement>(null);

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      {/* Stats cards */}
      <div className="mb-5 grid gap-5 sm:grid-cols-3">
        <StatCard icon="lucide:pill" label="Active Medications" value={activeMeds.length} />
        <StatCard icon="lucide:refresh-cw" label="Low on Refills" value={lowRefillMeds.length} />
        <StatCard icon="lucide:check-circle-2" label="Past Medications" value={inactiveMeds.length} />
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.3fr_1fr]">
        <div className="grid content-start gap-6">
          <MedicationGroup
            title="All" meds={medications} selectedId={selectedMedication?.id}
            onSelect={setSelectedMedication} groupRef={allRef}
          />
          <MedicationGroup
            title="Low on Refills" meds={lowRefillMeds} selectedId={selectedMedication?.id}
            onSelect={setSelectedMedication} groupRef={lowRefillsRef}
          />
          <MedicationGroup
            title="Past" meds={inactiveMeds} selectedId={selectedMedication?.id}
            onSelect={setSelectedMedication} groupRef={pastRef}
          />
        </div>

        <div>
          <MedicationDetailPanel medication={selectedMedication} onClose={() => setSelectedMedication(null)} />
        </div>
      </div>
    </div>
  );
}
